"use client";

import { ProfileDropdown } from "@/app/dashboard/(general)/_components/profile-dropdown";
import LinkLoadingIndicator from "@/components/loader/link-loading-indicator";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";

import { Spinner } from "@/components/loader/spinner";
import { useAuth } from "@/components/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { toLoginRedirect } from "@/feature/auth/lib/auth-redirect";
import { NotificationBell } from "@/feature/notifications/components/notification-bell";
import { Bell } from "lucide-react";

type HeaderAuthActionsProps = {
  mobile?: boolean;
};

export function HeaderAuthActions({ mobile = false }: HeaderAuthActionsProps) {
  const { isHydrated, isAuthenticated } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentPath = useMemo(() => {
    const nextPathname = pathname || "/";
    const query = searchParams.toString();

    return query ? `${nextPathname}?${query}` : nextPathname;
  }, [pathname, searchParams]);

  const loginHref = useMemo(() => toLoginRedirect(currentPath), [currentPath]);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="icon-sm"
          className="relative"
          aria-label="Open notifications"
          disabled={true}
        >
          <Bell className="size-4.5" />
        </Button>

        <Button
          variant="outline"
          size={mobile ? "default" : "sm"}
          className={mobile ? "w-full justify-start" : undefined}
          disabled
        >
          <Spinner />
        </Button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Button variant="default" size="sm" asChild>
        <Link href={loginHref}>
          <LinkLoadingIndicator title="Login" />
        </Link>
      </Button>
    );
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <NotificationBell />
      <ProfileDropdown />
    </div>
  );
}
